import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Scale,
  FileCheck,
  Shield,
  Briefcase,
  AlertTriangle,
  Cpu,
  CreditCard,
  Ban,
  HelpCircle,
  ChevronRight,
  Printer,
  Sparkles
} from "lucide-react";

export default function TermsPage() {
  const [activeSection, setActiveSection] = useState("acceptance");

  const lastUpdated = "October 8, 2026";
  const effectiveDate = "October 8, 2026";

  const handlePrint = () => {
    window.print();
  };

  const sections = [
    { id: "acceptance", label: "1. Acceptance & Eligibility" },
    { id: "services", label: "2. Scope of Services & Engagements" },
    { id: "user-obligations", label: "3. User Conduct & Inquiries" },
    { id: "intellectual-property", label: "4. Intellectual Property Rights" },
    { id: "ai-disclaimer", label: "5. AI & Automated Tools Disclaimer" },
    { id: "commercial-terms", label: "6. Commercials, Invoicing & Taxes" },
    { id: "confidentiality", label: "7. Confidentiality & Non-Disclosure" },
    { id: "disclaimers-liability", label: "8. Limitation of Liability" },
    { id: "termination", label: "9. Term, Suspension & Termination" },
    { id: "governing-law", label: "10. Governing Law & Indian Jurisdiction" },
    { id: "amendments", label: "11. Amendments & Legal Notices" },
  ];

  return (
    <main className="pt-28 md:pt-36 pb-20 bg-white">
      {/* Hero Header */}
      <section className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex flex-wrap items-center gap-2 text-[11px] tracking-[0.2em] font-semibold text-black/60 mb-3">
          <Link to="/" className="hover:text-black">HOME</Link>
          <ChevronRight className="h-3.5 w-3.5 text-black/30" />
          <span className="text-black">LEGAL</span>
          <ChevronRight className="h-3.5 w-3.5 text-black/30" />
          <span className="text-black">TERMS AND CONDITIONS</span>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 pb-8 border-b border-black/10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black text-white text-[11px] font-bold tracking-[0.15em] mb-4">
              <Scale className="h-3.5 w-3.5 text-amber-400" />
              TERMS OF SERVICE (INDIA &amp; GLOBAL)
            </div>
            <h1 className="font-orbitron font-black text-3xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Terms &amp; Conditions
              <br />
              <span className="italic font-normal text-black/60">and Service Agreement</span>
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base text-black/70 leading-relaxed">
              These Terms and Conditions govern your access to the BUGZ X platform, services, software deliverables, and automated growth systems. Please review them carefully.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-black/15 text-xs font-semibold hover:bg-black/5 transition-colors"
            >
              <Printer className="h-3.5 w-3.5" /> Print / PDF
            </button>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-black text-white text-xs font-semibold hover:bg-black/85 transition-colors"
            >
              Contact Legal Desk
            </Link>
          </div>
        </div>

        {/* Metadata summary bar */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 rounded-2xl bg-black/[0.02] border border-black/10 text-xs">
          <div>
            <p className="text-black/40 text-[10px] tracking-widest font-semibold uppercase">Governing Law</p>
            <p className="mt-1 font-bold">Republic of India</p>
          </div>
          <div>
            <p className="text-black/40 text-[10px] tracking-widest font-semibold uppercase">Last Updated</p>
            <p className="mt-1 font-bold">{lastUpdated}</p>
          </div>
          <div>
            <p className="text-black/40 text-[10px] tracking-widest font-semibold uppercase">Effective Date</p>
            <p className="mt-1 font-bold">{effectiveDate}</p>
          </div>
          <div>
            <p className="text-black/40 text-[10px] tracking-widest font-semibold uppercase">Dispute Resolution</p>
            <p className="mt-1 font-bold">Arbitration &amp; Conciliation Act, 1996</p>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar Nav */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 mt-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          
          {/* Sidebar Nav */}
          <aside className="lg:col-span-4">
            <div className="sticky top-28 p-5 rounded-3xl border border-black/12 bg-white/90 backdrop-blur shadow-sm space-y-2">
              <p className="text-[10px] tracking-[0.25em] font-bold text-black/50 uppercase px-2 mb-3">
                TABLE OF CONTENTS
              </p>
              <nav className="space-y-1">
                {sections.map((sec) => (
                  <a
                    key={sec.id}
                    href={`#${sec.id}`}
                    onClick={() => setActiveSection(sec.id)}
                    className={`block px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                      activeSection === sec.id
                        ? "bg-black text-white"
                        : "text-black/70 hover:bg-black/5 hover:text-black"
                    }`}
                  >
                    {sec.label}
                  </a>
                ))}
              </nav>

              <div className="pt-4 mt-4 border-t border-black/10">
                <div className="p-3 rounded-xl bg-black/[0.03] border border-black/10 text-[11px]">
                  <p className="font-bold flex items-center gap-1.5">
                    <FileCheck className="h-3.5 w-3.5 text-black shrink-0" />
                    Binding Legal Contract
                  </p>
                  <p className="mt-1 text-black/60 leading-relaxed">
                    By browsing, submitting inquiries, or engaging BUGZ X for services, you agree to be bound by these terms.
                  </p>
                </div>
              </div>
            </div>
          </aside>

          {/* Legal Text Body */}
          <div className="lg:col-span-8 space-y-12 text-sm text-black/80 leading-relaxed">
            
            {/* Section 1 */}
            <div id="acceptance" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  01
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Acceptance of Terms &amp; Eligibility
                </h2>
              </div>
              <div className="mt-5 space-y-4">
                <p>
                  These Terms and Conditions (&ldquo;Terms&rdquo;) constitute a legally binding agreement between you (&ldquo;Client&rdquo;, &ldquo;User&rdquo;, or &ldquo;You&rdquo;) and <strong>BUGZ X</strong> (&ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;), governing your use of our website (<a href="https://bugzx.space" className="underline font-semibold">https://bugzx.space</a>) and all commercial services provided by BUGZ X.
                </p>
                <p>
                  By accessing or using our website, submitting an inquiry, purchasing a service package, or executing a Statement of Work (SOW), you acknowledge that you have read, understood, and agree to be bound by these Terms and our <Link to="/privacy" className="underline font-semibold text-black">Privacy Policy</Link>.
                </p>
                <div className="p-4 rounded-2xl bg-black/[0.02] border border-black/10 text-xs">
                  <p className="font-bold text-black mb-1">Eligibility Criteria:</p>
                  <p className="text-black/70">
                    You represent and warrant that you are at least 18 years of age (the age of majority under Indian law) and possess the full legal authority, capacity, and power to enter into these Terms on your own behalf or on behalf of the commercial entity you represent.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 2 */}
            <div id="services" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  02
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Scope of Services &amp; Engagements
                </h2>
              </div>
              <div className="mt-5 space-y-4">
                <p>
                  BUGZ X provides professional technology and digital solutions including, but not limited to:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div className="p-3 rounded-2xl border border-black/10">
                    <p className="font-bold text-black">AI Solutions &amp; Automation</p>
                    <p className="mt-1 text-black/65">Chatbots, AI agents, document processing, workflow automation, and custom algorithmic integrations.</p>
                  </div>
                  <div className="p-3 rounded-2xl border border-black/10">
                    <p className="font-bold text-black">Custom Software &amp; ERPs</p>
                    <p className="mt-1 text-black/65">Full-stack web applications, SaaS platforms, portals, mobile apps, and enterprise management tools.</p>
                  </div>
                  <div className="p-3 rounded-2xl border border-black/10">
                    <p className="font-bold text-black">Growth &amp; Performance Marketing</p>
                    <p className="mt-1 text-black/65">Multi-channel paid acquisition, search engine optimization (SEO), and answer engine optimization (AEO).</p>
                  </div>
                  <div className="p-3 rounded-2xl border border-black/10">
                    <p className="font-bold text-black">Strategy &amp; Brand Systems</p>
                    <p className="mt-1 text-black/65">Go-to-market roadmaps, conversion rate optimization, visual identities, and architectural audits.</p>
                  </div>
                </div>
                <p className="text-xs text-black/70">
                  Specific deliverables, milestones, delivery timelines, and project-specific commercial terms are defined in distinct Statements of Work (SOW) or written proposals agreed upon between BUGZ X and the Client.
                </p>
              </div>
            </div>

            {/* Section 3 */}
            <div id="user-obligations" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  03
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  User Conduct &amp; Form Submissions
                </h2>
              </div>
              <div className="mt-5 space-y-4">
                <p>
                  When utilizing our website or submitting forms (inquiries, job applications, partnership proposals), you agree:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-xs text-black/75">
                  <li>To provide true, accurate, current, and complete personal and commercial information.</li>
                  <li>Not to upload or transmit any malicious code, viruses, automated spiders, scraping scripts, or files containing malware.</li>
                  <li>Not to impersonate any individual or entity or misrepresent your affiliation with any organization.</li>
                  <li>Not to upload any resume, document, or content that infringes upon third-party intellectual property or violates applicable laws.</li>
                  <li>That BUGZ X reserves the right to decline any inquiry or project request at its sole discretion without incurring liability.</li>
                </ul>
              </div>
            </div>

            {/* Section 4 */}
            <div id="intellectual-property" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  04
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Intellectual Property Rights
                </h2>
              </div>
              <div className="mt-5 space-y-4">
                <div className="space-y-3 text-xs">
                  <div className="p-4 rounded-2xl border border-black/10">
                    <p className="font-bold text-black text-sm">A. BUGZ X Proprietary Property</p>
                    <p className="mt-1 text-black/70 leading-relaxed">
                      All content, trademarks, logos, visual assets, 3D/cyber graphics, website UI/UX designs, algorithms, code libraries, and foundational frameworks created by BUGZ X remain the exclusive intellectual property of BUGZ X and are protected under Indian Copyright and Trademark laws.
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl border border-black/10">
                    <p className="font-bold text-black text-sm">B. Client Deliverables &amp; Custom Work</p>
                    <p className="mt-1 text-black/70 leading-relaxed">
                      Upon full and final payment of all agreed fees for custom development or design projects, all specific custom assets developed uniquely for the Client as specified in the relevant SOW shall be assigned to the Client, subject to standard carve-outs for BUGZ X pre-existing code and reusable open-source components.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5 */}
            <div id="ai-disclaimer" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  05
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  AI &amp; Automated Tools Disclaimer
                </h2>
              </div>
              <div className="mt-5 space-y-4">
                <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-xs space-y-2">
                  <p className="font-bold text-amber-950 flex items-center gap-1.5">
                    <Cpu className="h-4 w-4 text-amber-800" />
                    Nature of AI and Algorithmic Systems:
                  </p>
                  <p className="text-amber-900 leading-relaxed">
                    BUGZ X builds and leverages cutting-edge artificial intelligence, machine learning models, and automated workflow integrations. You acknowledge that AI systems produce probabilistic outputs. While BUGZ X implements rigorous guardrails and testing, AI-generated outputs, automated predictions, and third-party LLM behaviors cannot be guaranteed to be error-free in every edge scenario.
                  </p>
                </div>
                <p className="text-xs text-black/70">
                  Clients deploying autonomous customer-facing AI agents are advised to maintain human-in-the-loop oversight and appropriate operational safeguards.
                </p>
              </div>
            </div>

            {/* Section 6 */}
            <div id="commercial-terms" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  06
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Commercials, Invoicing &amp; Taxes
                </h2>
              </div>
              <div className="mt-5 space-y-4 text-xs text-black/80">
                <ul className="list-disc pl-5 space-y-2">
                  <li><strong>Quotations:</strong> All commercial quotes and project scopes remain valid for 30 calendar days from issuance unless otherwise specified.</li>
                  <li><strong>Milestone Payments:</strong> Projects typically follow agreed milestone schedules (e.g., initial deposit upon kickoff, intermediate delivery milestone, final sign-off).</li>
                  <li><strong>Taxation &amp; Invoicing:</strong> Invoices are issued in INR or USD as agreed. Indian GST (Goods and Services Tax) and applicable statutory taxes are levied as per Indian taxation laws.</li>
                  <li><strong>Late Payments:</strong> Overdue invoices may attract statutory interest and may lead to temporary suspension of active engineering sprints or hosting deployments.</li>
                </ul>
              </div>
            </div>

            {/* Section 7 */}
            <div id="confidentiality" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  07
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Confidentiality &amp; Non-Disclosure
                </h2>
              </div>
              <div className="mt-5 space-y-4 text-xs text-black/80">
                <p>
                  Both parties agree to treat all non-public technical information, business data, architecture diagrams, source code, and commercial strategies shared during the engagement as strictly <strong>Confidential Information</strong>.
                </p>
                <p>
                  Neither party shall disclose confidential information to any third party without prior written consent, except to employees, contractors, and legal advisors bound by equivalent confidentiality obligations.
                </p>
              </div>
            </div>

            {/* Section 8 */}
            <div id="disclaimers-liability" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  08
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Limitation of Liability &amp; Disclaimers
                </h2>
              </div>
              <div className="mt-5 space-y-4 text-xs text-black/80">
                <p className="uppercase font-semibold text-black/60">
                  DISCLAIMER OF UNWARRANTED GUARANTEES:
                </p>
                <p>
                  Except as expressly set forth in a signed agreement, our website and preliminary advisory materials are provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis without warranties of any kind.
                </p>
                <div className="p-4 rounded-2xl bg-black/[0.03] border border-black/10">
                  <p className="font-bold text-black mb-1">Cap on Monetary Liability:</p>
                  <p className="text-black/70 leading-relaxed">
                    To the maximum extent permitted by applicable Indian law, under no circumstances shall BUGZ X or its directors, officers, or contractors be liable for any indirect, incidental, consequential, punitive, or special damages (including loss of profits, data, or business goodwill). In any event, BUGZ X&rsquo;s aggregate liability arising out of any claim shall not exceed the total amount paid by the Client to BUGZ X for the specific service giving rise to the claim during the three (3) months preceding the event.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 9 */}
            <div id="termination" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  09
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Term, Suspension &amp; Termination
                </h2>
              </div>
              <div className="mt-5 space-y-4 text-xs text-black/80">
                <p>
                  BUGZ X reserves the right to suspend or terminate your access to the website or services immediately, without prior notice, if you violate these Terms, breach confidentiality, or engage in conduct harmful to BUGZ X or third parties.
                </p>
                <p>
                  Either party may terminate an ongoing SOW as per the notice period and exit criteria defined in the specific agreement.
                </p>
              </div>
            </div>

            {/* Section 10 */}
            <div id="governing-law" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border-2 border-black bg-white shadow-lg">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  10
                </span>
                <h2 className="font-orbitron font-black text-xl md:text-2xl text-black">
                  Governing Law &amp; Dispute Resolution (India)
                </h2>
              </div>
              <div className="mt-5 space-y-4 text-xs text-black/80 leading-relaxed">
                <p>
                  These Terms, their interpretation, and any disputes arising out of or in connection with them shall be governed by, construed, and enforced in accordance with the substantive <strong>Laws of the Republic of India</strong>, without giving effect to any conflict of law principles.
                </p>

                <div className="p-4 rounded-2xl bg-black text-white space-y-3">
                  <p className="font-orbitron font-bold text-sm text-white flex items-center gap-2">
                    <Scale className="h-4 w-4 text-amber-400" />
                    Arbitration Mechanism:
                  </p>
                  <p className="text-white/80 leading-relaxed">
                    Any dispute, controversy, or claim arising out of or relating to these Terms or the breach, termination, or invalidity thereof shall be referred to and finally resolved by binding arbitration in accordance with the <strong>Arbitration and Conciliation Act, 1996</strong> of India. The arbitration shall be conducted by a sole arbitrator mutually appointed by the parties. The seat and venue of arbitration shall be in India, and the proceedings shall be conducted in the English language.
                  </p>
                  <p className="text-white/60 text-[11px] pt-1">
                    Subject to arbitration, courts having jurisdiction in India shall have exclusive jurisdiction over any legal matters arising hereunder.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 11 */}
            <div id="amendments" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  11
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Amendments &amp; Legal Notices
                </h2>
              </div>
              <div className="mt-5 space-y-4 text-xs text-black/80">
                <p>
                  We may revise these Terms from time to time. Any changes will be posted on this page with an updated &ldquo;Last Updated&rdquo; date. Your continued use of the website or services following notice of changes constitutes your acceptance of the revised Terms.
                </p>
                <div className="p-4 rounded-2xl bg-black/[0.02] border border-black/10">
                  <p className="font-bold text-black mb-1">Legal Communications &amp; Official Inquiries:</p>
                  <p className="text-black/70">
                    For legal notices or questions regarding these Terms, please contact our Legal Desk at <a href="mailto:team@bugzx.space" className="underline font-bold text-black">team@bugzx.space</a> or <a href="mailto:grievance@bugzx.space" className="underline font-bold text-black">grievance@bugzx.space</a>.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </main>
  );
}
